"use client";

import React from "react";
import { Phone, Clock, MapPin, ShieldAlert, FileCheck2, CircleAlert as AlertCircle } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
interface FaqItem {
  id: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  question: string;
  answer: string;
}
const FAQS: FaqItem[] = [{
  id: "item-1",
  icon: Clock,
  question: "What is TriServe's emergency response time in Frederick County?",
  answer: "Our average local emergency deployment window across Frederick County is approximately 60 minutes. Crews mobilize via direct access points along the I-70 and US-15 transit corridors, allowing our certified field technicians to reach residences and commercial facilities quickly for urgent water extraction, board-up, and structural stabilization."
}, {
  id: "item-2",
  icon: MapPin,
  question: "Which geographic areas of Frederick County does TriServe serve?",
  answer: "TriServe provides comprehensive emergency response across all of Frederick County. This includes historic downtown Frederick, Ballenger Creek, Urbana, Walkersville, Middletown, Thurmont, Myersville, Brunswick, and the communities bordering the Monocacy River and Catoctin Ridge foothills."
}, {
  id: "item-3",
  icon: FileCheck2,
  question: "How does TriServe coordinate with insurance adjusters on property claims?",
  answer: "TriServe works directly alongside your insurance adjuster by providing standardized moisture mapping, thermal imaging telemetry, and detailed line-item scoping. We supply full digital photographic documentation directly to the carrier to verify property damage, helping expedite your claim while maintaining clear third-party transparency."
}, {
  id: "item-4",
  icon: AlertCircle,
  question: "What immediate steps should a resident take after discovering water, fire, or mold damage?",
  answer: "First, prioritize personal safety: shut off the main water valve if dealing with a leak, or turn off the HVAC system to prevent smoke or mold spores from circulating. Avoid standing water near electrical sockets or appliances. Do not disturb visible mold colonies. Contact our 24/7 mobile dispatch line immediately so field crews can contain the spread."
}, {
  id: "item-5",
  icon: ShieldAlert,
  question: "Does TriServe have a physical walk-in office in Frederick, MD?",
  answer: "No. TriServe operates strictly through dedicated 24/7 mobile field deployment units. We do not operate a walk-in retail office or public showroom in Maryland. Fully stocked mitigation and remediation vehicles are dispatched directly from our regional staging hubs to your property address."
}, {
  id: "item-6",
  icon: Phone,
  question: "How do I reach TriServe for 24/7 emergency service in Frederick County?",
  answer: "You can contact our emergency dispatch hotline at 855-435-6631 at any hour of the day or night, every day of the year. Calls are answered directly by local coordination specialists who dispatch on-duty technicians immediately to your residential or commercial site."
}];
export default function FrederickFaqs() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map(faq => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer
      }
    }))
  };
  return <section id="frederickcountyfaqs" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-t border-border">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center space-y-4 mb-12 sm:mb-16">
          <div className="flex justify-center">
            <Badge variant="outline" className="border-border bg-muted/50 text-foreground px-3 py-1 text-xs sm:text-sm font-medium">
              Frederick County Guidance
            </Badge>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground">
            Frederick County Restoration FAQs
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Essential facts on regional emergency response times, corridor coverage, mobile dispatch, and claims coordination across Frederick County, Maryland.
          </p>
        </div>

        {/* Accordion Component */}
        <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden p-2 sm:p-4 mb-10">
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((item, index) => {
            const IconComp = item.icon;
            return <AccordionItem key={item.id} value={item.id} data-index={index} className="border-b border-border/80 last:border-b-0 px-2 sm:px-4">
                  <AccordionTrigger className="hover:no-underline py-5 text-left group">
                    <span className="flex items-center gap-3 text-base sm:text-lg font-medium text-foreground group-hover:text-primary transition-colors">
                      <span className="p-2 rounded-md bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary transition-colors shrink-0">
                        <IconComp className="size-4 sm:size-5" />
                      </span>
                      {item.question}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pl-11 pr-2 pb-6">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>;
          })}
          </Accordion>
        </div>

        {/* Emergency Callout Banner */}
        <div className="rounded-lg border border-border bg-muted/40 p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center sm:text-left">
            <h3 className="text-lg font-semibold text-foreground">
              Experiencing active property damage in Frederick County?
            </h3>
            <p className="text-sm text-muted-foreground">
              Our mobile units are on standby 24 hours a day, 365 days a year.
            </p>
          </div>
          <div className="shrink-0 w-full sm:w-auto">
            <Button asChild size="lg" className="w-full sm:w-auto font-medium">
              <a href="tel:855-435-6631" className="inline-flex items-center justify-center gap-2">
                <Phone className="size-4" />
                <span>Call 855-435-6631</span>
              </a>
            </Button>
          </div>
        </div>

        {/* FAQPage JSON-LD Schema */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify(faqSchema)
      }} />
      </div>
    </section>;
}